/**
 * AdminNeedsAttention — Operational Intelligence V1 (May 24, 2026).
 *
 * Doctrine (operator-defined):
 *   • Calm. Actionable. Low-clutter. Truthful.
 *   • Rule-based, admin-only.
 *   • Admin actions: resolve, ignore, reopen, open affected area.
 *
 * The page is a single table with quiet severity badges, filter
 * chips, and one expandable detail panel per row showing
 * likely_cause, suggested_action, audit_log, and a deep-link to
 * the affected area.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, AlertCircle, Info, CheckCircle2, EyeOff, RotateCcw,
  ExternalLink, RefreshCcw, Crown, ChevronRight, Clock,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY = {
  critical: { label: "Critical", icon: AlertTriangle, color: "rose",  cls: "bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-700" },
  high:     { label: "High",     icon: AlertCircle,  color: "amber", cls: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700" },
  medium:   { label: "Medium",   icon: Info,         color: "blue",  cls: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700" },
  low:      { label: "Low",      icon: Info,         color: "slate", cls: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700" },
};

const STATUS = {
  open:     { label: "Open",     cls: "bg-amber-50 text-amber-800 border-amber-200" },
  ignored:  { label: "Ignored",  cls: "bg-slate-50 text-slate-600 border-slate-200" },
  resolved: { label: "Resolved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const sec = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default function AdminNeedsAttention() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ open_total: 0, by_severity: {} });
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filterStatus, setFilterStatus] = useState("open");
  const [filterSeverity, setFilterSeverity] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterSeverity) params.set("severity", filterSeverity);
      params.set("limit", "200");
      const [list, c] = await Promise.all([
        axios.get(`${API}/api/admin/needs-attention?${params}`, { withCredentials: true }),
        axios.get(`${API}/api/admin/needs-attention/count`, { withCredentials: true }),
      ]);
      setItems(list.data?.items || []);
      setCounts(c.data || { open_total: 0, by_severity: {} });
    } catch (e) {
      toast.error("Couldn't load operational issues.");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSeverity]);

  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/api/admin/needs-attention/scan`, {}, { withCredentials: true });
      toast.success(`Scan complete · ${r.data?.total_issues ?? 0} issues across ${r.data?.rule_results?.length ?? 0} rules`);
      await load();
    } catch {
      toast.error("Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const transition = async (id, action) => {
    try {
      await axios.post(`${API}/api/admin/needs-attention/${id}/${action}`, {}, { withCredentials: true });
      toast.success(`Issue ${action === "resolve" ? "resolved" : action === "ignore" ? "ignored" : "reopened"}`);
      await load();
    } catch {
      toast.error(`Couldn't ${action} issue`);
    }
  };

  const sevCount = (s) => counts.by_severity?.[s] || 0;

  return (
    <div data-testid="admin-needs-attention" className="min-h-dvh bg-slate-50 dark:bg-slate-950 pb-12">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/admin" className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm">← Admin</Link>
          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Crown className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-extrabold text-slate-900 dark:text-white text-base leading-tight">Needs Attention</p>
            <p className="text-[11px] text-slate-500 leading-tight">Operational intelligence · rule-based · admin-only</p>
          </div>
          <button
            type="button"
            data-testid="needs-attention-scan-button"
            onClick={runScan}
            disabled={scanning}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${scanning ? "animate-spin" : ""}`} />
            {scanning ? "Scanning…" : "Run scan now"}
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 pt-5 space-y-4">
        {/* Severity summary row */}
        <div data-testid="needs-attention-summary" className="grid grid-cols-4 gap-2">
          {["critical", "high", "medium", "low"].map((s) => {
            const meta = SEVERITY[s];
            const n = sevCount(s);
            const Icon = meta.icon;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setFilterSeverity(filterSeverity === s ? "" : s)}
                data-testid={`needs-attention-sev-${s}`}
                className={`rounded-lg border px-3 py-3 text-left transition ${
                  filterSeverity === s
                    ? "ring-2 ring-offset-1 ring-slate-400 dark:ring-offset-slate-950"
                    : ""
                } ${meta.cls}`}
              >
                <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest font-bold opacity-80">
                  <Icon className="w-3 h-3" />{meta.label}
                </div>
                <div className="text-2xl font-extrabold mt-1 tabular-nums">{n}</div>
              </button>
            );
          })}
        </div>

        {/* Status filter chips */}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">Status:</span>
          {["open", "ignored", "resolved", ""].map((st) => (
            <button
              key={st || "all"}
              type="button"
              data-testid={`needs-attention-filter-${st || "all"}`}
              onClick={() => setFilterStatus(st)}
              className={`px-2.5 py-1 rounded-md border transition ${
                filterStatus === st
                  ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                  : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700"
              }`}
            >
              {st === "" ? "All" : st[0].toUpperCase() + st.slice(1)}
            </button>
          ))}
        </div>

        {/* Issue list */}
        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div data-testid="needs-attention-empty" className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-12 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Nothing needs attention.</p>
            <p className="text-xs text-slate-500 mt-1">Quiet. Calm. Operational.</p>
          </div>
        ) : (
          <ul data-testid="needs-attention-list" className="space-y-2">
            {items.map((it) => {
              const sev = SEVERITY[it.severity] || SEVERITY.low;
              const stat = STATUS[it.status] || STATUS.open;
              const SevIcon = sev.icon;
              const isOpen = expanded === it.id;
              return (
                <li
                  key={it.id}
                  data-testid={`needs-attention-row-${it.id}`}
                  className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : it.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${sev.cls}`}>
                      <SevIcon className="w-2.5 h-2.5" />{sev.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{it.issue_type}</p>
                      <p className="text-[11.5px] text-slate-500 truncate">
                        {it.affected?.label || it.rule_id} · <Clock className="inline w-3 h-3 -mt-px" /> {relativeTime(it.last_seen_at)}
                      </p>
                    </div>
                    <span className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${stat.cls}`}>
                      {stat.label}
                    </span>
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition ${isOpen ? "rotate-90" : ""}`} />
                  </button>

                  {isOpen && (
                    <div data-testid={`needs-attention-detail-${it.id}`} className="px-4 pb-4 pt-1 space-y-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/30">
                      <div className="grid sm:grid-cols-2 gap-3 text-[12.5px]">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Likely cause</p>
                          <p className="text-slate-700 dark:text-slate-300 leading-snug">{it.likely_cause || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Suggested action</p>
                          <p className="text-slate-700 dark:text-slate-300 leading-snug">{it.suggested_action || "—"}</p>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        {it.affected?.route && (
                          <Link
                            to={it.affected.route}
                            data-testid={`needs-attention-open-${it.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Open {it.affected.kind === "system_repair_incident" ? "incident" : it.affected.kind}
                          </Link>
                        )}
                        {it.status === "open" && (
                          <>
                            <button
                              type="button"
                              data-testid={`needs-attention-resolve-${it.id}`}
                              onClick={() => transition(it.id, "resolve")}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />Mark resolved
                            </button>
                            <button
                              type="button"
                              data-testid={`needs-attention-ignore-${it.id}`}
                              onClick={() => transition(it.id, "ignore")}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100"
                            >
                              <EyeOff className="w-3.5 h-3.5" />Ignore
                            </button>
                          </>
                        )}
                        {(it.status === "ignored" || it.status === "resolved") && (
                          <button
                            type="button"
                            data-testid={`needs-attention-reopen-${it.id}`}
                            onClick={() => transition(it.id, "reopen")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />Reopen
                          </button>
                        )}
                      </div>

                      {/* Audit log */}
                      {Array.isArray(it.audit_log) && it.audit_log.length > 0 && (
                        <details className="text-[11.5px]">
                          <summary className="cursor-pointer text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                            Audit log · {it.audit_log.length} {it.audit_log.length === 1 ? "entry" : "entries"}
                          </summary>
                          <ul className="mt-2 space-y-1 text-slate-600 dark:text-slate-400 font-mono">
                            {it.audit_log.map((ev, idx) => (
                              <li key={idx}>
                                <span className="text-slate-400">{new Date(ev.ts).toISOString().slice(0, 19).replace("T", " ")}</span>
                                {" · "}
                                <span className="font-bold">{ev.action}</span>
                                {" · "}
                                <span>{ev.actor}</span>
                                {ev.detail ? ` · ${ev.detail}` : ""}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}

                      <p className="text-[10px] text-slate-400 font-mono">rule={it.rule_id} · id={it.id}</p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
